import React, { useEffect } from 'react';
import { NextPageWithLayout, GetServerSideProps } from 'next';
import { QueryClient } from '@tanstack/react-query';
import { ProductPageQueryKeys } from '@enums/react-query-keys';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import extractFromContext from '@helpers/utils/extract-from-context';
import DefaultLayout from '@layouts/default-layout';
import ProductService from '@services/product-service';
import { NegotiationProvider } from '@context/negotiation.context';
import { ChatProvider } from '@context/chat.context';
import { usePreferences } from '@context/preferences.context';
import PageAuthenticationGuard from '@components/atoms/authentication/page-authentication-guard/page-authentication-guard';
import { Product } from '@interfaces/models/product';
import { isRequest404Error, isRequestError } from '@helpers/errors';
import logger from '@helpers/utils/logger/client';
import Head from '@components/atoms/head/head';
import { defaultMetas } from '@constants/metas/default';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { UpdateDataLayerComponent } from '@context/analytics.context';
import { getAppInitialProps } from '@helpers/initial-props';
import { Shipping } from '@interfaces/models/shipping';
import MessageList from './messageList';
import styles from './index.module.scss';

const getBuyerAndProduct = (query) => {
  if (!query || !query.id) {
    return {};
  }

  const result: string[] = query.id.split('-');

  if (Array.isArray(result) && result.length === 2) {
    const [buyerId, productId] = result;
    return { buyerId, productId };
  } else {
    return {};
  }
};

export type ChatPageProps = {
  product: Product;
  chatId: string;
  shipping: Shipping;
};

const ChatPage: NextPageWithLayout<ChatPageProps> = (props) => {
  const { product, chatId, shipping } = props;
  const { language } = usePreferences();
  const pageMetas = defaultMetas[language];
  const { sendAnalyticEvent: sendChatAnalyticEvents } = useAnalyticEvents('chat');

  const dataLayer = {
    screen_name: '/buyer_seller_chat',
    screen_category: 'chat',
    screen_subcategory: 'buyer_seller_chat',
    product_id: product?.id,
    seller_id: product?.seller?.id,
    product_universe: product?.universe?.name,
    product_category: product?.category?.name,
    product_sub_category: product?.subcategory?.name,
    product_brand: product?.brand?.name,
    product_country: product?.seller?.country,
    product_unit_price: ((product?.price?.cents ?? 0) / 100).toString(),
    product_ds_eligible: product?.dsEligible,
    product_crossborder_transaction: `${product?.dutyAndTax?.cents > 0}`,
    product_currency: product?.price?.currency,
    product_sold: product?.sold,
  };

  useEffect(() => {
    sendChatAnalyticEvents('product_impression');
  }, []);

  return (
    <PageAuthenticationGuard redirectionUrl={'/'}>
      <UpdateDataLayerComponent data={dataLayer} />
      <Head metadata={pageMetas} />
      <div className={styles.chatPageContainer}>
        <NegotiationProvider>
          <ChatProvider
            product={product}
            mode={'inline'}
            shipping={shipping}
          >
            <MessageList
              product={product}
              chatId={chatId}
            />
          </ChatProvider>
        </NegotiationProvider>
      </div>
    </PageAuthenticationGuard>
  );
};

ChatPage.Layout = [DefaultLayout];
ChatPage.PageType = 'ChatPage';

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { query } = context;
  const { locale, serverAxiosRequestData } = extractFromContext(context);
  const queryClient = new QueryClient();

  try {
    const { buyerId, productId } = getBuyerAndProduct(query);

    const [initialProps, translations, product, shipping] = await Promise.all([
      getAppInitialProps(context),
      serverSideTranslations(locale),
      ...(productId ? [ProductService.getProductWithBuyerId(productId, buyerId, serverAxiosRequestData)] : []),
      ...(productId ? [ProductService.getProductShippingInformation(productId, serverAxiosRequestData)] : []),
    ]);

    queryClient.setQueryData([ProductPageQueryKeys.PRODUCT_SHIPPING], shipping);

    return {
      props: {
        ...initialProps,
        ...translations,
        product: product || null,
        buyerId: buyerId || null,
        chatId: query.id || null,
        shipping: shipping || null,
      },
    };
  } catch (err) {
    if (err?.config?.url?.includes('/products/')) {
      return {
        redirect: { destination: '/chat', permanent: false },
      };
    }

    if (isRequestError(err)) {
      if (!isRequest404Error(err)) {
        logger.error(err, 'There was an api request error on the Chat Page - getServerSideProps - index.page.tsx');
      }
      return {
        notFound: true,
      };
    }
    logger.error(err, 'The server failed to render the Chat Page - getServerSideProps - index.page.tsx');
    throw err;
  }
};

export default ChatPage;
